import each from 'lodash/each';
import get from 'lodash/get';

import { defaultMapping } from '@/features/Clients/ClientMapper/mappingDefinitions/default';
import type { DefaultMappingKeys } from '@/features/Clients/ClientMapper/mappingDefinitions/default';
import { mitversicherterPartnerMapping } from '@/features/Clients/ClientMapper/mappingDefinitions/mitversicherterPartner';
import type { MitversicherterPartnerMappingKeys } from '@/features/Clients/ClientMapper/mappingDefinitions/mitversicherterPartner';
import { versichertePersonMapping } from '@/features/Clients/ClientMapper/mappingDefinitions/versichertePerson';
import type { VersichertePersonMappingKeys } from '@/features/Clients/ClientMapper/mappingDefinitions/versichertePerson';
import { versicherungsnehmerMapping } from '@/features/Clients/ClientMapper/mappingDefinitions/versicherungsnehmer';
import type { VersicherungsnehmerMappingKeys } from '@/features/Clients/ClientMapper/mappingDefinitions/versicherungsnehmer';
import { zahlpersonMapping } from '@/features/Clients/ClientMapper/mappingDefinitions/zahlperson';
import type { ZahlpersonMappingKeys } from '@/features/Clients/ClientMapper/mappingDefinitions/zahlperson';
import { ROLE } from '@/features/Clients/types';
import type {
  ClientResource,
  ClientResourceKeys,
} from '@/features/Clients/types';
import { getIsoDateString, parseDate } from '@/utils/dates/dates';

export const clientMappings: {
  [key in ROLE | 'default']: Record<string, string>;
} = {
  default: defaultMapping,

  [ROLE.VERSICHERUNGSNEHMER]: versicherungsnehmerMapping,
  [ROLE.ZAHLPERSON]: zahlpersonMapping,
  [ROLE.VERSICHERTE_PERSON]: versichertePersonMapping,
  [ROLE.MITVERSICHERTER_PARTNER]: mitversicherterPartnerMapping,
};

export type MappingKeys =
  | DefaultMappingKeys
  | VersicherungsnehmerMappingKeys
  | ZahlpersonMappingKeys
  | VersichertePersonMappingKeys
  | MitversicherterPartnerMappingKeys;

export const mapToApiClient = <T extends string = MappingKeys>(
  client: ClientResource,
  mapping: keyof typeof clientMappings = 'default',
): Record<T, unknown> => {
  const mappedClient: Partial<Record<T, string>> = {};

  each(clientMappings[mapping], (value, key) => {
    if (key === 'birthday' || key === 'fuehrerscheindatum') {
      const bd = get(client, value) as Date | null;

      if (bd === null) {
        return;
      }

      mappedClient[key as T] = getIsoDateString(bd);
    } else {
      mappedClient[key as T] = get(client, value) as string;
    }
  });

  return mappedClient as Record<T, unknown>;
};

export const mapToClientResource = <T extends string = ClientResourceKeys>(
  client: unknown,
  mapping: keyof typeof clientMappings = 'default',
): ClientResource => {
  const res: Partial<Record<T, string | undefined | null | Date>> = {};

  each(clientMappings[mapping], (key, value) => {
    if (key === 'geburtsdatum' || key === 'fuehrerscheindatum') {
      const dateValue: string | null = get(client, value, null) as
        | string
        | null;

      if (dateValue === null) {
        res[key as T] = dateValue;
      } else {
        res[key as T] = parseDate(dateValue);
      }
    } else {
      res[key as T] = get(client, value);
    }
  });

  return res as ClientResource;
};
